import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ILiquidityRequest, TAssetType } from "domains/liquidity/shared/types";
import { liquidityApiReducer } from "./liquidity.api.reducer";

export interface IInitialState {
  liquidity_request_id: string | null;
  isAddManually: boolean;
  isLA: boolean;
  currentAsset: Partial<TAssetType>;
  assetsPendingCreation: TAssetType[];
  assetsPendingCustodyAccount: string[];
  selectedAssetsForLiquidity: string[];
  selectedAssetsData: TAssetType[];
  LRAssets: any[];
  currentLiquidityRequest: ILiquidityRequest | null;
  currentAssetCustodyAccount: string | null;
  isFetching: boolean | null;
  error: any;
  LRDocuments: any;
}

const initialState: IInitialState = {
  liquidity_request_id: null,
  isAddManually: false,
  isLA: false,
  currentAsset: {},
  assetsPendingCreation: [],
  assetsPendingCustodyAccount: [],
  selectedAssetsForLiquidity: [],
  selectedAssetsData: [],
  LRAssets: [],
  currentLiquidityRequest: null,
  currentAssetCustodyAccount: null,
  isFetching: null,
  error: null,
  LRDocuments: null,
};

const liquidityReducer = createSlice({
  name: "[Liquidity]",
  initialState,
  reducers: {

    setLRAssets(state, action) {
      state.LRAssets = action.payload;
    },
    setLRDocuments(state, action) {
      state.LRDocuments = action.payload;
    },
    setIsFetching(state, action) {
      state.isFetching = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },

    setIsLA: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isLA: action.payload,
    }),

    setIsAddManually: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isAddManually: action.payload,
    }),

    setCurrentAssetData: (state, action: PayloadAction<Partial<TAssetType>>) => ({
      ...state,
      currentAsset: {
        ...state.currentAsset,
        ...action.payload,
      },
    }),
    setCurrentAssetCustodyAccount: (state, action: PayloadAction<string>) => {
      return ({
      ...state,
      currentAssetCustodyAccount: action.payload,
    })},
    resetCurrentAssetData: (state) => ({
      ...state,
      currentAsset: {},
    }),

    setAssetsPendingCustodyAccount: (state, action: PayloadAction<any>) => {
      const assetsPendingCustodyAccount = state.assetsPendingCustodyAccount
      ? [ ...state.assetsPendingCustodyAccount, action.payload ]
      : [ action.payload ]
      return ({
        ...state,
        assetsPendingCustodyAccount,
      })
    },
    resetAssetsPendingCustodyAccount: (state) => {
      return ({
      ...state,
      assetsPendingCustodyAccount: [],
    })},
    setAssetsPendingCreation: (state, action: PayloadAction<TAssetType>) => ({
      ...state,
      assetsPendingCreation: [ ...state.assetsPendingCreation, action.payload ],
      currentAsset: {},
    }),


    resetAssetsPendingCreation: (state) => {
      return ({
      ...state,
      assetsPendingCreation: [],
    })},

    setSelectedAssetsForLiquidity: (state, action: PayloadAction<any>) => ({
      ...state,
      selectedAssetsForLiquidity: action.payload,
    }),

    setSelectedAssetsData: (state, action: PayloadAction<TAssetType[]>) => ({
      ...state,
      selectedAssetsData: action.payload,
    }),

    unsetSelectedAssetsForLiquidity: (state, action: PayloadAction<any>) => {
      state.selectedAssetsForLiquidity = state.selectedAssetsForLiquidity.filter(
        (selected) => selected !== action.payload,
      );
    },

    resetSelectedAssetsForLiquidity: (state) => {
      return ({
      ...state,
      selectedAssetsForLiquidity: [],
    })},

    setAssetsRemoveById: (state, action: PayloadAction<string>) => {
      const assetsPendingCreation = state.assetsPendingCreation.filter(
        (asset) => asset.asset_id !== action.payload,
      )
      return ({
      ...state,
      assetsPendingCreation,
    }) },
    setCurrentLiquidityRequestData: (state, action: PayloadAction<Partial<ILiquidityRequest>>) => {
      return ({
      ...state,
      currentLiquidityRequest: {
        ...state.currentLiquidityRequest,
        ...action.payload,
      } as ILiquidityRequest,
    })},

    resetCurrentLiquidityRequestData: (state) => ({
      ...state,
      currentLiquidityRequest: null,
    }),

    setLiquidityReducerData: (state, action: PayloadAction<Partial<IInitialState>>) => ({
      ...state,
      ...action.payload,
    }),

    resetLiquidityReducer: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(liquidityApiReducer.endpoints.createLRAsset.matchFulfilled, (state) => ({
      ...state,
      currentAsset: initialState.currentAsset,
    })),
      builder.addMatcher(liquidityApiReducer.endpoints.fetchLRAssets.matchPending, (state, action) => ({
        ...state,
        liquidity_request_id: action.meta.arg.originalArgs,
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.updateLRAsset.matchFulfilled, (state) => ({
        ...state,
        currentAsset: initialState.currentAsset,
      })),
      // Preserve selected assets during LR creation to avoid losing formal proposal selections
      builder.addMatcher(liquidityApiReducer.endpoints.createLiquidityRequest.matchPending, (state, action) => {
        const shouldPreserveAssets = state.selectedAssetsForLiquidity?.length > 0 && 
                                     state.selectedAssetsData?.length > 0;
        
        return {
          ...initialState,
          ...(shouldPreserveAssets ? {
            selectedAssetsForLiquidity: state.selectedAssetsForLiquidity,
            selectedAssetsData: state.selectedAssetsData,
          } : {}),
          ...action.meta.arg.originalArgs,
        };
      }),
      builder.addMatcher(liquidityApiReducer.endpoints.createLiquidityRequest.matchFulfilled, (state, action) => ({
        ...state,
        liquidity_request_id: action.payload,
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.fetchLiquidityRequest.matchPending, (state, action) => ({
        ...state,
        currentLiquidityRequest:
          state?.currentLiquidityRequest?.id === action.meta.arg.originalArgs
            ? { ...state.currentLiquidityRequest }
            : ({ id: action.meta.arg.originalArgs } as ILiquidityRequest),
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.fetchLiquidityRequest.matchFulfilled, (state, action) => ({
        ...state,
        currentLiquidityRequest: { ...state.currentLiquidityRequest, ...action.payload },
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.createAssetTrustben.matchPending, (state, action) => ({
        ...state,
        assetsPendingCreation: state.assetsPendingCreation.map((asset) =>
          asset.asset_id.includes(action.meta.arg.originalArgs.asset.asset_id.split("_").at(-1)!)
            ? { ...asset, asset_id: action.meta.arg.originalArgs.asset.asset_id }
            : asset,
        ),
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.createAssetTrustben.matchFulfilled, (state, action) => ({
        ...state,
        assetsPendingCreation: state.assetsPendingCreation.filter(
          (asset) => asset.asset_id !== action.payload.dummyAssetId,
        ),
      })),
      builder.addMatcher(liquidityApiReducer.endpoints.createAssetTrustben.matchRejected, (state, action) => ({
        ...state,
        assetsPendingCreation: state.assetsPendingCreation.filter(
          (asset) => asset.asset_id !== action.meta.arg.originalArgs.asset.asset_id,
        ),
      }));
  },
});

export const {
  setIsAddManually,
  setCurrentAssetData,
  resetCurrentAssetData,
  setAssetsPendingCreation,
  resetAssetsPendingCreation,
  setCurrentAssetCustodyAccount,
  setCurrentLiquidityRequestData,
  resetCurrentLiquidityRequestData,
  resetAssetsPendingCustodyAccount,
  unsetSelectedAssetsForLiquidity,
  resetSelectedAssetsForLiquidity,
  setAssetsPendingCustodyAccount,
  setSelectedAssetsForLiquidity,
  setSelectedAssetsData,
  setLiquidityReducerData,
  resetLiquidityReducer,
  setAssetsRemoveById,
  setLRDocuments,
  setIsFetching,
  setLRAssets,
  setIsLA,
} = liquidityReducer.actions;

export default liquidityReducer.reducer;
